import { httpClient, PaginationParams } from '../core/http';

export interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  organization_id: number;
  user_id: number;
  status: 'active' | 'archived' | 'deleted';
}

export interface StartConversationRequest {
  message: string;
  crew_id: number;
}

export interface StartConversationResponse {
  conversation_id: number;
  session_id: string;
  message: string;
}

export interface ConversationMessage {
  id: number;
  conversation_id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export interface HistoryItemResponse {
  id: number;
  crew_id: number;
  organization_id: number;
  user_id?: number | null;
  session_id: string;
  user_message: string;
  ai_response: string;
  use_canvas?: boolean;
  rag_context?: string | null;
  rag_sources?: Record<string, unknown>[] | null;
  inputs?: Record<string, unknown> | null;
  summary?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  is_active: boolean;
}

export interface HistoryListResponse {
  items: HistoryItemResponse[];
}

export interface HistorySessionSummaryResponse {
  session_id: string;
  latest_history_id: number;
  crew_id: number;
  last_user_message: string;
  last_ai_response: string;
  turns: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface HistorySessionListResponse {
  items: HistorySessionSummaryResponse[];
}

export class ConversationService {
  private basePath = '/v1/conversations';

  /**
   * Inicia uma nova conversa com streaming
   * @param request Dados da conversa
   * @returns Stream de resposta
   */
  async startConversationStream(
    request: StartConversationRequest
  ): Promise<ReadableStream> {
    return httpClient.stream(`${this.basePath}/start-stream`, request);
  }

  /**
   * Lista todas as conversas do usuário
   * @returns Lista de conversas
   */
  async listConversations(pagination?: PaginationParams): Promise<Conversation[]> {
    const response = await httpClient.get<
      | Conversation[]
      | {
          items: Conversation[];
        }
    >(this.basePath, {
      params: pagination,
    });

    if (Array.isArray(response.data)) {
      return response.data;
    }

    return response.data.items || [];
  }

  /**
   * Obtém uma conversa específica
   * @param conversationId ID da conversa
   * @returns Dados da conversa
   */
  async getConversation(conversationId: number): Promise<Conversation> {
    const response = await httpClient.get<Conversation>(
      `${this.basePath}/${conversationId}`
    );
    return response.data;
  }

  /**
   * Lista histórico de interações do usuário.
   * @returns Lista de itens do histórico
   */
  async listHistory(): Promise<HistoryItemResponse[]> {
    const response = await httpClient.get<HistoryListResponse>('/v1/history/list');
    return response.data.items || [];
  }

  /**
   * Obtém um item específico do histórico.
   * @param historyId ID do histórico
   * @returns Item detalhado do histórico
   */
  async getHistoryById(historyId: number): Promise<HistoryItemResponse> {
    const response = await httpClient.get<HistoryItemResponse>(
      `/v1/history/${historyId}`
    );
    return response.data;
  }

  /**
   * Lista histórico agregado por sessão.
   */
  async listHistorySessions(): Promise<HistorySessionSummaryResponse[]> {
    const response = await httpClient.get<HistorySessionListResponse>(
      '/v1/history/sessions'
    );
    return response.data.items || [];
  }

  /**
   * Obtém todos os itens de uma sessão de histórico.
   */
  async getHistoryBySessionId(sessionId: string): Promise<HistoryItemResponse[]> {
    const response = await httpClient.get<HistoryListResponse>(
      `/v1/history/session/${encodeURIComponent(sessionId)}`
    );
    return response.data.items || [];
  }

  /**
   * Inicia uma nova conversa com streaming (versão string)
   * @param request Dados da conversa
   * @returns Stream de resposta como string
   */
  async startConversationStreamString(
    request: StartConversationRequest
  ): Promise<AsyncIterable<string>> {
    const stream = await this.startConversationStream(request);
    const reader = stream.getReader();
    const decoder = new TextDecoder();

    return {
      async *[Symbol.asyncIterator]() {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };
  }
}

export const conversationService = new ConversationService();
